import {createApi} from '@reduxjs/toolkit/query/react';
import {axiosBaseQuery, REFETCH_POLICY} from 'src/axiosBaseQuery';
import {
    ActiveTransactionDTO,
    ActiveTransactionRequest,
    ChangeChargerConfigurationRequest,
    ChargePointSettingDTO,
    OcppCommandResponse,
    OcppLogsSearchRequest,
    PageOcppLogDTO,
    ResetChargerRequest,
    StartChargingRequest,
    StopChargingRequest,
} from 'src/types';
import {getBaseURL} from 'src/utils/helper';

// initialize an empty api service that we'll inject endpoints into later as needed
export const ocppApi = createApi({
    reducerPath: 'ocppApi',
    ...REFETCH_POLICY,
    baseQuery: axiosBaseQuery({
        baseUrl: String(getBaseURL()),
    }),
    tagTypes: ['Configurations', 'ActiveTransaction'],
    endpoints: (build) => ({
        // Both invalidate the active session they just changed. The tag was declared and provided
        // and nothing ever invalidated it, so the session a card showed was whatever it had been
        // told once - a stop left the finished session on screen until the page was reopened.
        startCharging: build.mutation<OcppCommandResponse, StartChargingRequest>({
            query: (data) => ({url: '/ocpp/operations/startCharging', method: 'post', data}),
            invalidatesTags: ['ActiveTransaction'],
        }),
        stopCharging: build.mutation<OcppCommandResponse, StopChargingRequest>({
            query: (data) => ({url: '/ocpp/operations/stopCharging', method: 'post', data}),
            invalidatesTags: ['ActiveTransaction'],
        }),
        resetCharger: build.mutation<{ message: string }, ResetChargerRequest>({
            query: (data) => ({url: '/ocpp/operations/resetCharger', method: 'post', data}),
        }),
        changeConfigurations: build.mutation<{ message: string }, ChangeChargerConfigurationRequest>({
            query: (data) => ({url: '/ocpp/operations/changeConfiguration', method: 'post', data}),
            invalidatesTags: ['Configurations'],
        }),
        getConfigurations: build.query<
            ChargePointSettingDTO[],
            { chargerId: number; businessId: number; companyId?: number }
        >({
            query: ({chargerId, businessId, companyId}) => ({
                url: `/chargePoints/${chargerId}/configurations`,
                method: 'get',
                params: {businessId, ...(companyId && {companyId})},
            }),
            providesTags: ['Configurations'],
        }),
        /**
         * The cable lock, read and written through endpoints that can reach nothing else.
         *
         * The switch used to borrow the general configuration calls, so showing it required
         * `chargers.readConfig` and using it required `chargers.configure` — the right to inspect and
         * reprogram the device. Both now answer to `chargers.cableLock`, which is granted to the
         * people whose cable it is.
         */
        getCableLock: build.query<
            ChargePointSettingDTO[],
            { chargerId: number; businessId: number; companyId?: number }
        >({
            query: ({chargerId, businessId, companyId}) => ({
                url: `/chargePoints/${chargerId}/cable-lock`,
                method: 'get',
                params: {businessId, ...(companyId && {companyId})},
            }),
            providesTags: ['Configurations'],
        }),
        setCableLock: build.mutation<
            { message: string },
            { chargerId: number; businessId: number; companyId?: number; locked: boolean }
        >({
            query: (data) => ({url: '/ocpp/operations/cableLock', method: 'post', data}),
            invalidatesTags: ['Configurations'],
        }),
        getActiveTransaction: build.query<ActiveTransactionDTO, ActiveTransactionRequest>({
            query: (params) => ({url: '/transactions/active', method: 'get', params}),
            providesTags: ['ActiveTransaction'],
        }),
        searchOcppLogs: build.query<PageOcppLogDTO, OcppLogsSearchRequest>({
            query: ({data, params}) => ({
                url: '/ocpp_logs/search',
                method: 'post',
                data,
                params,
            }),
        }),
    }),
});
